'use client';

import type { ReactNode } from 'react';

import type { ImageType } from '@eliadmin/core/src/api/api-types';
import {
  STAGE_ICON,
  type OrderStage,
} from '@eliadmin/core/src/components/nt/account/orders/order-stage';
import type { ReorderResult } from '@eliadmin/core/src/components/nt/account/orders/use-reorder';
import { Icon, type IconName } from '@eliadmin/core/src/components/nt/ui/icon';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Figma text actions („Sledovať zásielku“, „Faktúra (PDF)“): Body/M Bold #0E6A20, underlined. */
export const TEXT_LINK =
  'nt-button-m rounded text-[#0E6A20] underline underline-offset-2 transition-colors hover:text-[#0B5719] focus-visible:shadow-focus focus-visible:outline-none disabled:cursor-default disabled:text-[#8F978E]';

/** Figma card (cards of the detail, order cards): white, 1 px #E2E5DF, radius 16. */
export const CARD = 'rounded-2xl border border-[#E2E5DF] bg-white';

/** Neutral pill when the administration sets no colour (Figma „Zrušená“ pill). */
const NEUTRAL = { background: '#EBEDE8', text: '#363D36' };

/**
 * Figma „Status“ (207:11671): the shop's status name on a pill in the colours of the status in
 * the administration (`color` / `color_text`), icon by the stage. 27 px, radius 999.
 */
export function OrderStatusBadge({
  status,
  stage,
  className,
}: {
  status: { name?: string | null; color?: string | null; color_text?: string | null };
  stage: OrderStage;
  className?: string;
}) {
  if (!status.name) return null;
  const background = status.color || NEUTRAL.background;
  const color = status.color_text || (status.color ? '#0D110D' : NEUTRAL.text);

  return (
    <span
      className={cn(
        'nt-caption-semibold inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full py-1 pl-2 pr-2.5',
        className,
      )}
      style={{ backgroundColor: background, color }}
    >
      <Icon name={STAGE_ICON[stage]} size={14} />
      {status.name}
    </span>
  );
}

/** Figma „Thumb“: product photo fitted on white, 1 px #E2E5DF, radius 12. */
export function OrderThumb({
  image,
  alt = '',
  className,
}: {
  image?: Pick<ImageType, 'url'> | null;
  alt?: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'relative block size-14 shrink-0 overflow-hidden rounded-xl border border-[#E2E5DF] bg-white',
        className,
      )}
    >
      {image?.url ? (
        <Image src={image.url} alt={alt} fill sizes="56px" className="object-contain" />
      ) : (
        <Icon
          name="package"
          size={24}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[#CDD2CA]"
        />
      )}
    </span>
  );
}

/** Confirm the atomic repeat, or explain the rejection/uncertain result before another attempt. */
export function ReorderMessage({ result }: { result: ReorderResult | null }) {
  if (!result) return null;

  return (
    <div
      role={result.success ? 'status' : 'alert'}
      className={cn(
        'nt-body-m flex flex-col gap-2 rounded-xl p-3',
        result.success ? 'bg-[#F5F6F3] text-[#0E6A20]' : 'bg-[#FFF4F2] text-[#B42318]',
      )}
    >
      {result.messages.map(message => (
        <p key={message}>{message}</p>
      ))}
      {result.showCart ? (
        <Link href={ROUTES.CART()} prefetch={false} className={TEXT_LINK}>
          Otvoriť košík
        </Link>
      ) : null}
    </div>
  );
}

/**
 * Empty, „nothing found“, error and not-found states – Figma has none: the soft panel of the help
 * box (bg #F5F6F3, radius 16) with a 40 px brand icon, a Heading/H6 line and the action.
 */
export function OrdersMessage({
  icon,
  title,
  action,
  alert,
}: {
  icon: IconName;
  title: string;
  action?: ReactNode;
  /** Errors are announced right away. */
  alert?: boolean;
}) {
  return (
    <div
      role={alert ? 'alert' : undefined}
      className="flex flex-col items-center gap-4 rounded-2xl bg-[#F5F6F3] px-4 py-10 text-center md:px-6"
    >
      <Icon name={icon} size={40} className="text-[#127A26]" />
      <p className="nt-h6 text-[#0D110D]">{title}</p>
      {action}
    </div>
  );
}
